import React, { Component } from 'react';
import { observer } from 'mobx-react';
import { observable, action, computed } from 'mobx';
import { MilestoneStore, PlanningTicketStore } from 'store/planning';
import { ProjectStore } from 'store/Project';
import { UserStore } from 'store/User';
import { SprintPlanningTab } from '../../component/SprintPlanning/SprintPlanningTab';
import { MilestoneProgressTab } from '../../component/SprintPlanning/MilestoneProgressTab';
import { ProjectTimelineTab } from '../../component/SprintPlanning/ProjectTimelineTab';
import './sprint-planning.css';

type TabType = 'planning' | 'progress' | 'timeline';

@observer
export class SprintPlanningScreen extends Component {
    milestoneStore = new MilestoneStore({ relations: ['project'] });
    ticketStore = new PlanningTicketStore({ relations: ['milestone', 'assignee'] });
    projectStore = new ProjectStore();
    userStore = new UserStore();

    @observable activeTab: TabType = 'planning';
    @observable isLoading = false;
    @observable error: string | null = null;

    async componentDidMount() {
        await this.loadData();
    }

    loadData = async () => {
        this.isLoading = true;
        this.error = null;
        try {
            await Promise.all([
                this.milestoneStore.fetch({ with: ['project'] }).catch((e: any) => console.warn('milestone fetch failed', e)),
                this.ticketStore.fetch({ with: ['milestone', 'assignee'] }).catch((e: any) => console.warn('ticket fetch failed', e)),
                this.projectStore.fetch({ with: ['company'] }).catch((e: any) => console.warn('project fetch failed', e)),
                this.userStore.fetch().catch((e: any) => console.warn('user fetch failed', e)),
            ]);
        } catch (error) {
            console.error('Failed to load sprint planning data:', error);
            this.error = 'Failed to load data.';
        } finally {
            this.isLoading = false;
        }
    };

    @action
    setTab = (tab: TabType) => {
        this.activeTab = tab;
    };

    @computed
    get visibleProjects() {
        return this.projectStore.models.filter((p: any) => !p.hidden);
    }

    render() {
        return (
            <div className="sp-app" data-test-sprint-planning-screen>
                <div className="sp-header">
                    <h1>Sprint Planning</h1>
                    <div className="sp-header-actions">
                        <button
                            className="sp-btn sp-btn-sm"
                            onClick={this.loadData}
                            data-test-refresh
                        >
                            ↻ Refresh
                        </button>
                    </div>
                </div>

                <div className="sp-tabs">
                    <button
                        className={`sp-tab ${this.activeTab === 'planning' ? 'active' : ''}`}
                        onClick={() => this.setTab('planning')}
                        data-test-tab="planning"
                    >
                        Sprint Planning
                    </button>
                    <button
                        className={`sp-tab ${this.activeTab === 'progress' ? 'active' : ''}`}
                        onClick={() => this.setTab('progress')}
                        data-test-tab="progress"
                    >
                        Milestone Progress
                    </button>
                    <button
                        className={`sp-tab ${this.activeTab === 'timeline' ? 'active' : ''}`}
                        onClick={() => this.setTab('timeline')}
                        data-test-tab="timeline"
                    >
                        Project Timeline
                    </button>
                </div>

                <div className="sp-main">
                    {this.error && <div className="sp-error">{this.error}</div>}
                    {this.isLoading ? (
                        <div className="sp-loading">Loading Sprint Planning...</div>
                    ) : (
                        <>
                            {this.activeTab === 'planning' && (
                                <SprintPlanningTab
                                    milestoneStore={this.milestoneStore}
                                    ticketStore={this.ticketStore}
                                    projectStore={this.projectStore}
                                    userStore={this.userStore}
                                    visibleProjects={this.visibleProjects}
                                />
                            )}
                            {this.activeTab === 'progress' && (
                                <MilestoneProgressTab
                                    milestoneStore={this.milestoneStore}
                                />
                            )}
                            {this.activeTab === 'timeline' && (
                                <ProjectTimelineTab
                                    milestoneStore={this.milestoneStore}
                                />
                            )}
                        </>
                    )}
                </div>
            </div>
        );
    }
}

export default SprintPlanningScreen;
